import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { AppButton, cn } from "../ui";
import { iconFor } from "./tokens";

/**
 * SetupSection — the activation checklist, as a section of the drawer.
 *
 * ── WHY IT SHRANK ──────────────────────────────────────────────────────────
 * This was a full-width card on the dashboard with eight tiles in a 4-column
 * grid. It was the right content in the wrong place: it sat above the KPI row
 * for every institute, and the institute that no longer needs it is the one
 * that has to scroll past it every day. In a drawer, vertical space is cheap
 * (it scrolls) and horizontal space is not, so the grid becomes a list.
 *
 * ── THE TWO DECISIONS THAT SURVIVED THE MOVE ────────────────────────────────
 *
 *  1. **The first incomplete step is marked, and it is the ONLY one marked.**
 *     A list of eight equal rows is a menu, and a menu is what the welcome
 *     modal already was — the thing people close without picking anything.
 *     "Start here" turns it back into an instruction. The server orders the
 *     steps by dependency (a batch needs a course), so "first incomplete" is
 *     always a step that can actually succeed right now.
 *  2. **Done steps stay clickable.** They recede to a check and muted ink, but
 *     they remain links, because "add your students" is a thing you do on day
 *     one and again every intake.
 *
 * ── AND ONE THAT IS NEW ─────────────────────────────────────────────────────
 * Completed steps are COLLAPSED behind a count. On day one that is nothing; by
 * the time someone is five steps in, the two that remain are what they came for
 * and five green ticks are just something to read past. The progress bar
 * already carries "you are mostly done" — the list does not need to say it
 * again.
 *
 * ⚠️ `steps` is already filtered server-side by plan and by the viewer's
 * permissions, so `total` is the number of steps THIS person can complete.
 * Never render a fraction over a fixed 8 — an institute without WhatsApp in its
 * plan would sit at "7 of 8" for ever, with no way to find the eighth.
 */
const SetupSection = ({ setup, onHide }) => {
  const navigate = useNavigate();
  const [showDone, setShowDone] = useState(false);
  const { steps = [], completed = 0, total = 0 } = setup || {};

  if (!steps.length || completed >= total) return null;

  const nextId = steps.find((s) => !s.done)?.id;
  const visible = showDone ? steps : steps.filter((s) => !s.done);

  return (
    <section aria-labelledby="ac-setup-heading">
      <div className="tw-flex tw-items-center tw-gap-2 tw-bg-surface-alt tw-px-4 tw-py-2">
        <h3
          id="ac-setup-heading"
          className="tw-m-0 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle"
        >
          Set up your institute
        </h3>
        <span className="tw-ml-auto tw-text-[0.6875rem] tw-font-semibold tw-text-content-muted">
          {completed} / {total}
        </span>
      </div>

      {/* Segmented, not continuous: eight segments read as eight things, which
          is the promise being made. `aria-hidden` because the fraction above
          announces the same value — a progressbar role would say it twice. */}
      <div className="tw-flex tw-gap-1 tw-px-4 tw-pt-3" aria-hidden="true">
        {steps.map((s) => (
          <span
            key={s.id}
            className={cn("tw-h-1 tw-flex-1 tw-rounded-sm", s.done ? "tw-bg-primary" : "tw-bg-line")}
          />
        ))}
      </div>

      <ul className="tw-m-0 tw-list-none tw-p-0">
        {visible.map((step) => {
          const Icon = iconFor(step.icon);
          const isNext = step.id === nextId;

          return (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => navigate(step.to)}
                className={cn(
                  "tw-group tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-3 tw-border-0 tw-bg-transparent tw-px-4 tw-py-2.5 tw-text-left tw-transition tw-focus-ring hover:tw-bg-surface-hover",
                )}
              >
                <span
                  className={cn(
                    "tw-mt-0.5 tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-sm",
                    step.done ? "tw-bg-success/15 tw-text-success" : "tw-bg-primary/10 tw-text-primary",
                  )}
                >
                  {step.done ? <Check size={13} aria-hidden="true" /> : <Icon size={13} aria-hidden="true" />}
                </span>

                <span className="tw-min-w-0 tw-flex-1">
                  <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">
                    <span
                      className={cn(
                        "tw-text-[0.8125rem] tw-font-semibold",
                        step.done ? "tw-text-content-muted" : "tw-text-content",
                      )}
                    >
                      {step.title}
                    </span>
                    {isNext && (
                      <span className="tw-rounded-sm tw-bg-primary-solid tw-px-1.5 tw-py-0.5 tw-text-[0.5625rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-primary-solid-fg">
                        Start here
                      </span>
                    )}
                  </span>
                  {!step.done && (
                    <span className="tw-mt-0.5 tw-block tw-text-xs tw-leading-relaxed tw-text-content-muted">
                      {step.description}
                    </span>
                  )}
                </span>

                <ArrowRight
                  size={14}
                  aria-hidden="true"
                  className="tw-mt-1 tw-shrink-0 tw--translate-x-1 tw-text-primary tw-opacity-0 tw-transition group-hover:tw-translate-x-0 group-hover:tw-opacity-100"
                />
              </button>
            </li>
          );
        })}
      </ul>

      <div className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-pb-2">
        {completed > 0 && (
          <AppButton variant="ghost" size="xs" onClick={() => setShowDone((v) => !v)}>
            {showDone ? "Hide completed" : `Show ${completed} completed`}
          </AppButton>
        )}
        <AppButton variant="ghost" size="xs" className="tw-ml-auto" onClick={onHide}>
          Hide this guide
        </AppButton>
      </div>
    </section>
  );
};

export default SetupSection;
